import type { ReactNode } from "react";

import type { ExperienceType } from "../dtos/experiences";

export const ModKeysList = [
  "tieredDiscount",
  "cartDiscount",
  "splitUrl",
  "price",
  "theme",
  "template",
  "freeGift",
  "freeShipping",
  "buyXGetY",
  "onsiteInjections",
  "onsiteEdits",
  "onsiteUpsell",
  "shipping",
  "shippingProgressBar",
  "priceStayAi",
  "goals",
  "offer",
  "checkoutBlock",
  "checkoutBlockPlaceholder",
  "postPurchase",
  "postPurchasePlaceholder",
] as const;

export type ModKeys = (typeof ModKeysList)[number];

export type ModConfig = {
  key: ModKeys;
  condition: boolean;
  component: ReactNode;
};

// null mods here are eligible for propensity to buy (purchase likelihood) audience targeting
export const PROPENSITY_MOD_POLICY = {
  onsiteEdits: "content edit",
  onsiteInjections: "styles & javascript",
  price: "price",
  // Only ever configured alongside `price`.
  priceStayAi: "price",
  splitUrl: "redirect",
  template: "template",
  theme: "theme",
  buyXGetY: null,
  cartDiscount: null,
  checkoutBlock: null,
  checkoutBlockPlaceholder: null,
  freeGift: null,
  freeShipping: null,
  goals: null,
  offer: null,
  onsiteUpsell: null,
  postPurchase: null,
  postPurchasePlaceholder: null,
  shipping: null,
  shippingProgressBar: null,
  tieredDiscount: null,
} as const satisfies Record<ModKeys, string | null>;

// null types here are eligible for propensity to buy (purchase likelihood) audience targeting
// note that experiences of eligible type become ineligible when ineligible mods are applied
export const PROPENSITY_TYPE_POLICY = {
  "content/advanced": "content",
  "content/onsiteEdits": "content",
  "content/template": "content",
  "content/theme": "content",
  "content/url": "content",
  pricing: "price",
  checkoutBlock: null,
  onsiteUpsell: null,
  personalization: null,
  "personalization/offer": null,
  postPurchase: null,
  shipping: null,
  sitewide: null,
} as const satisfies Record<ExperienceType, string | null>;

export type PropensityIneligibleMod = {
  [K in ModKeys]: (typeof PROPENSITY_MOD_POLICY)[K] extends null ? never : K;
}[ModKeys];

export const formatPropensityIneligibleReason = (
  mods: ModKeys[],
  experienceType: ExperienceType | undefined
): string | undefined => {
  // certain types make an experience ineligible.
  const typeLabel = experienceType
    ? PROPENSITY_TYPE_POLICY[experienceType]
    : null;
  if (typeLabel) {
    return `Purchase likelihood targeting is unavailable on ${typeLabel} experiences.`;
  }

  // experiences of eligible type become ineligible when ineligible mods are applied
  const labels = [
    ...new Set(
      mods
        .map((mod): string | null => PROPENSITY_MOD_POLICY[mod])
        .filter((label): label is string => label !== null)
    ),
  ];
  if (!labels.length) return undefined;
  const list =
    labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
  return `Purchase likelihood targeting is unavailable on experiences with a ${list} modification.`;
};
